import { action, computed, makeObservable, observable, runInAction } from "mobx";
import {
  deactivateAccountSendEMail,
  clearLocalStorageToken,
  downloadFile,
  EditProfileType,
  ProfileApiData,
  Model,
  FilePreview,
  editProfile,
  getAffirmationTemplate,
  getProfile,
  setTwoFactor,
  setTwoFactorAlways,
  updateProfileSettings,
  ProfileSettingsPayload,
  ToastType
} from "@lubbezposrednio-frontend/core";
import { NavigateFunction } from "react-router-dom";
import { AxiosError } from "axios";

const AFFIRMATION_ERRORS = [
  {
    responseMessage: "Sent file is not the original affirmation",
    toastMessage:
      "Błędne oświadczenie. W polu oświadczenie dodaj podpisany cyfrowo plik oświadczenia, które pobrałeś z naszej strony. Nie możesz modyfikować jego treści inaczej niż podpisem."
  },
  {
    responseMessage: "File is not signed by profil zaufany",
    toastMessage:
      "Błędne oświadczenie, nie zostało prawidłowo podpisane. W polu oświadczenie dodaj podpisany cyfrowo plik oświadczenia."
  }
];

export class ProfileViewModel extends Model {
  userProfilePhoto: FilePreview | null = null;
  organizationProfilePhoto: FilePreview | null = null;
  profileStatement: File | null = null;
  organizationStatement: File | null = null;
  firstName = "";
  lastName = "";
  email = "test@test.com";
  phoneNumber = "";
  postCode = "";
  organizationName = "";
  organizationAdress = "";
  organizationCity = "";
  organizationPostCode = "";
  organizationPhoneNumber = "";
  organizationNip = "";
  organizationKrs = "";
  organizationRegon = "";
  isOrganization = false;
  userPhotoPreview: string | null = null;
  organizationPhotoPreview: string | null = null;
  statementUrl: string | null = null;
  organizationStatementUrl: string | null = null;
  isUserVerified = false;
  isOrganisationVerified = false;
  apiAccess = false;
  twoFactorEnabled = false;
  twoFactorSaving = false;
  totpEnabled = false; // 3. metoda 2FA: kod z aplikacji authenticator
  identityKey = false; // czy konto ma portfel tożsamości (król w hierarchii bezpieczeństwa)
  twoFactorAlways = false; // polityka: ZAWSZE pytaj o 2FA (ignoruj zaufane urządzenie)
  passkeysCount = 0;
  cyberidLockedFields: string[] = []; // pola zweryfikowane przez CyberID (A2+) — read-only

  profilePublic = false;
  profileRef: string | null = null;
  profileBio = "";
  profileShowVotings = true;
  profileShowGroups = true;
  profileShowEmail = false;
  profileShowOrgDetails = true;
  profileShowAddress = true;
  profileShowPosts = true;
  allowGroupAdd = true;
  followersCount = 0;
  profileSaving = false;

  constructor() {
    super();

    makeObservable(this, {
      userProfilePhoto: observable,
      organizationProfilePhoto: observable,
      profileStatement: observable,
      organizationStatement: observable,
      firstName: observable,
      lastName: observable,
      email: observable,
      phoneNumber: observable,
      postCode: observable,
      organizationName: observable,
      organizationAdress: observable,
      organizationCity: observable,
      organizationPostCode: observable,
      organizationPhoneNumber: observable,
      organizationNip: observable,
      organizationKrs: observable,
      organizationRegon: observable,
      isOrganization: observable,
      userPhotoPreview: observable,
      organizationPhotoPreview: observable,
      statementUrl: observable,
      organizationStatementUrl: observable,
      isUserVerified: observable,
      isOrganisationVerified: observable,
      apiAccess: observable,
      twoFactorEnabled: observable,
      twoFactorSaving: observable,
      totpEnabled: observable,
      identityKey: observable,
      twoFactorAlways: observable,
      passkeysCount: observable,
      cyberidLockedFields: observable,
      toggleTwoFactorAlways: action.bound,
      setTotpEnabled: action.bound,
      profilePublic: observable,
      profileRef: observable,
      profileBio: observable,
      profileShowVotings: observable,
      profileShowGroups: observable,
      profileShowEmail: observable,
      profileShowOrgDetails: observable,
      profileShowAddress: observable,
      profileShowPosts: observable,
      allowGroupAdd: observable,
      followersCount: observable,
      profileSaving: observable,
      setProfileBio: action.bound,
      saveProfileSettings: action.bound,
      toggleTwoFactor: action.bound,
      setUserProfilePhoto: action.bound,
      setOrganizationProfilePhoto: action.bound,
      setProfileStatement: action.bound,
      setOrganizationStatement: action.bound,
      loadProfileData: action.bound,
      isUserStatementUploaded: computed
    });
  }

  override async onInit() {}

  async loadProfileData({ user }: ProfileApiData) {
    const baseUrl = process.env.REACT_APP_BACKEND_URL;

    if (user.is_organization) {
      this.organizationPhotoPreview = user.avatar_url && baseUrl + user.avatar_url;
      this.organizationName = user.organization_name ?? "";
      this.organizationNip = user.nip ?? "";
      this.organizationKrs = user.krs ?? "";
      this.organizationRegon = user.regon ?? "";
      this.organizationPostCode = user.zip_code ?? "";
      this.organizationAdress = user.address ?? "";
      this.organizationCity = user.city ?? "";
      this.organizationPhoneNumber = user.phone_number ?? "";
      this.organizationStatementUrl = user.affirmation_url && baseUrl + user.affirmation_url;
      this.isOrganisationVerified = !!user.verified;

      this.userPhotoPreview = user.representatives_avatar_url && baseUrl + user.representatives_avatar_url;
      this.firstName = user.representatives_first_name ?? "";
      this.lastName = user.representatives_last_name ?? "";
      this.phoneNumber = user.representatives_phone_number ?? "";
      this.postCode = user.representatives_zip_code ?? "";
      this.statementUrl = user.representatives_affirmation_url && baseUrl + user.representatives_affirmation_url;
      this.isUserVerified = !!user.representatives_verified;
    } else {
      this.userPhotoPreview = user.avatar_url && baseUrl + user.avatar_url;
      this.firstName = user.first_name ?? "";
      this.lastName = user.last_name ?? "";
      this.phoneNumber = user.phone_number ?? "";
      this.postCode = user.zip_code ?? "";
      this.statementUrl = user.affirmation_url && baseUrl + user.affirmation_url;
      this.isUserVerified = !!user.verified;
    }

    this.email = user.email;
    this.isOrganization = !!user.is_organization;
    this.apiAccess = !!user.api_access;
    this.twoFactorEnabled = !!user.two_factor_enabled;
    this.totpEnabled = !!(user as { totp_enabled?: boolean }).totp_enabled;
    this.identityKey = !!(user as { identity_key?: boolean }).identity_key;
    this.twoFactorAlways = !!(user as { two_factor_always?: boolean }).two_factor_always;
    this.passkeysCount = (user as { passkeys_count?: number }).passkeys_count ?? 0;
    this.cyberidLockedFields = (user as { cyberid_locked_fields?: string[] }).cyberid_locked_fields ?? [];

    this.profilePublic = !!user.profile_public;
    this.profileRef = user.profile_ref ?? null;
    this.profileBio = user.profile_bio ?? "";
    this.profileShowVotings = user.profile_show_votings ?? true;
    this.profileShowGroups = user.profile_show_groups ?? true;
    this.profileShowEmail = user.profile_show_email ?? false;
    this.profileShowOrgDetails = user.profile_show_org_details ?? true;
    this.profileShowAddress = user.profile_show_address ?? true;
    this.profileShowPosts = user.profile_show_posts ?? true;
    this.allowGroupAdd = user.allow_group_add ?? true;
    this.followersCount = user.followers_count ?? 0;
  }

  setProfileBio(value: string) {
    this.profileBio = value;
  }

  // Zapis ustawień profilu publicznego (toggle/przełączniki/bio). Odświeża stan z odpowiedzi.
  async saveProfileSettings(fields: ProfileSettingsPayload, toast: ToastType) {
    this.profileSaving = true;
    try {
      const r = await updateProfileSettings(fields);
      const user = r.data.user;
      runInAction(() => {
        this.profilePublic = !!user.profile_public;
        this.profileRef = user.profile_ref ?? null;
        this.profileBio = user.profile_bio ?? "";
        this.profileShowVotings = user.profile_show_votings ?? true;
        this.profileShowGroups = user.profile_show_groups ?? true;
        this.profileShowEmail = user.profile_show_email ?? false;
      });
    } catch {
      toast({ title: "Nie udało się zapisać ustawień profilu.", status: "error", isClosable: true });
    } finally {
      runInAction(() => {
        this.profileSaving = false;
      });
    }
  }

  // Włącz/wyłącz 2FA. Backend odrzuci wyłączenie przy api_access (wtedy i tak locked w UI).
  async toggleTwoFactor(enabled: boolean, toast: ToastType) {
    this.twoFactorSaving = true;
    try {
      await setTwoFactor(enabled);
      runInAction(() => {
        this.twoFactorEnabled = enabled;
      });
    } catch {
      toast({ title: "Nie udało się zmienić ustawienia 2FA.", status: "error", isClosable: true });
    } finally {
      runInAction(() => {
        this.twoFactorSaving = false;
      });
    }
  }

  async toggleTwoFactorAlways(enabled: boolean, toast: ToastType) {
    this.twoFactorSaving = true;
    try {
      await setTwoFactorAlways(enabled);
      runInAction(() => {
        this.twoFactorAlways = enabled;
      });
    } catch {
      toast({ title: "Nie udało się zmienić ustawienia.", status: "error", isClosable: true });
    } finally {
      runInAction(() => {
        this.twoFactorSaving = false;
      });
    }
  }

  // Po enrollmencie/wyłączeniu authenticatora (TOTP). Włączenie TOTP włącza też 2FA konta (backend to robi).
  setTotpEnabled(enabled: boolean) {
    this.totpEnabled = enabled;
    if (enabled) this.twoFactorEnabled = true;
  }

  public async getProfileData(toast: ToastType) {
    try {
      const response = await getProfile();
      if (response.status === 200) {
        return this.loadProfileData(response.data);
      }
      this.showGenericErrorToast(toast);
    } catch (err) {
      this.showGenericErrorToast(toast);
    }
  }

  setUserProfilePhoto(photo: FilePreview) {
    this.userProfilePhoto = photo;
    // Natychmiastowy podgląd wybranego/przyciętego zdjęcia (do tej pory podgląd brał tylko zapisany avatar).
    if (photo.src) this.userPhotoPreview = photo.src;
  }

  setOrganizationProfilePhoto(photo: FilePreview) {
    this.organizationProfilePhoto = photo;
    if (photo.src) this.organizationPhotoPreview = photo.src;
  }

  setProfileStatement(file: File) {
    this.profileStatement = file;
  }

  setOrganizationStatement(file: File) {
    this.organizationStatement = file;
  }

  public async downloadAffirmationTemplate() {
    const response = await getAffirmationTemplate();
    if (response?.status === 200 && response?.data) {
      downloadFile(response?.data, "oswiadczenie.pdf");
    }
  }

  // Pole zablokowane przez weryfikację CyberID (A2+) — read-only w UI; backend i tak odrzuca zmianę.
  isFieldLocked(field: string): boolean {
    return this.cyberidLockedFields.includes(field);
  }

  private getSavedData() {
    const savedData = {} as EditProfileType;

    if (this.isOrganization) {
      savedData.organization_name = this.organizationName;
      savedData.krs = this.organizationKrs;
      savedData.nip = this.organizationNip;
      savedData.regon = this.organizationRegon;
      savedData.address = this.organizationAdress;
      savedData.city = this.organizationCity;
      savedData.affirmation = this.organizationStatement;
      savedData.zip_code = this.organizationPostCode;
      savedData.phone_number = this.organizationPhoneNumber;
      savedData.avatar = this.organizationProfilePhoto;
      savedData.representatives_affirmation = this.profileStatement;
      savedData.representatives_avatar = this.userProfilePhoto;
      savedData.representatives_phone_number = this.phoneNumber;
      savedData.representatives_zip_code = this.postCode;
      savedData.representatives_first_name = this.firstName;
      savedData.representatives_last_name = this.lastName;
    } else {
      savedData.zip_code = this.postCode;
      savedData.avatar = this.userProfilePhoto;
      savedData.affirmation = this.profileStatement;
      savedData.phone_number = this.phoneNumber;
      // Pola zweryfikowane przez CyberID są read-only — nie wysyłamy ich (backend i tak odrzuci zmianę).
      if (!this.isFieldLocked("first_name")) savedData.first_name = this.firstName;
      if (!this.isFieldLocked("last_name")) savedData.last_name = this.lastName;
    }

    return savedData;
  }

  public async saveProfileData(toast: ToastType) {
    try {
      const response = await editProfile(this.getSavedData());
      if (response.status === 200) {
        const baseUrl = process.env.REACT_APP_BACKEND_URL;
        const user = response.data?.user;

        if (this.isOrganization) {
          this.statementUrl = user?.representatives_affirmation_url
            ? baseUrl + user?.representatives_affirmation_url
            : undefined;
          this.organizationStatementUrl = user?.affirmation_url ? baseUrl + user?.affirmation_url : undefined;
        } else {
          this.statementUrl = user?.affirmation_url ? baseUrl + user?.affirmation_url : undefined;
        }

        return toast({ status: "success", isClosable: true, title: "Zmiany zostały zapisane" });
      }
    } catch (err: any) {
      const affirmationErrors = [
        ...(err?.response?.data?.errors?.affirmation ?? []),
        ...(err?.response?.data?.errors?.representatives_affirmation ?? [])
      ];

      const affirmationErrorMessage =
        (err as AxiosError)?.response?.status === 422 &&
        AFFIRMATION_ERRORS.find(affirmationErr => affirmationErrors?.includes?.(affirmationErr.responseMessage))
          ?.toastMessage;

      const detailedErrors: Record<string, string[]> = err?.response?.data?.errors;
      const detailedMessages =
        detailedErrors && Object.entries(detailedErrors)?.map(([key, messages]) => `${key}: ${messages?.join(", ")}`);

      this.showGenericErrorToast(
        toast,
        affirmationErrorMessage ||
          `Nie udało się zapisać zmian, spróbuj ponownie później. Treść błędu: ${detailedMessages ?? err?.message}`
      );
    }
  }

  public async deactivateAccount(toast: ToastType, navigate: NavigateFunction) {
    try {
      const response = await deactivateAccountSendEMail();
      if (response.status === 202) {
        navigate("/wyslanie-maila-z-potwierdzeniem-usuniecia-konta");
        clearLocalStorageToken();
      }
    } catch (err: any) {
      this.showGenericErrorToast(toast);
    }
  }

  get isUserStatementUploaded(): boolean {
    return !!(this.statementUrl || this.profileStatement);
  }
}
